import { useEffect, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { TextField, MenuItem } from '@mui/material'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { PropertyFieldProps } from './PropertyField'

type PropertySelectProps = PropertyFieldProps

export default function PropertySelect({
  propertyId,
  property,
  required,
  shrinkLabel,
  variant,
}: PropertySelectProps) {
  const { field } = useController({
    name: propertyId,
    rules: { required },
  })

  const propertyOptionsConfig = property.options

  const { formState } = useFormContext()
  const dependentFieldValue = useWatch({
    name: propertyOptionsConfig.dependent_property,
    disabled: !propertyOptionsConfig.dependent_property,
  })

  const dependentFieldLength = useMemo(() => {
    if (dependentFieldValue && Array.isArray(dependentFieldValue)) {
      return dependentFieldValue.length
    }
    return 0
  }, [dependentFieldValue])

  const { errors } = formState
  const { display } = property

  const options: {
    label: string
    value: string
  }[] = useMemo(() => {
    if (Array.isArray(propertyOptionsConfig)) {
      return propertyOptionsConfig
    }
    if (dependentFieldLength) {
      const tempOptions = Array(dependentFieldLength)
        .fill(null)
        .map((_, index) => ({
          label: `Option ${index + 1}`,
          value: String(index + 1),
        }))
      if (!required) {
        tempOptions.unshift({ label: 'None', value: '' })
      }
      return tempOptions
    }
    return []
  }, [dependentFieldLength, propertyOptionsConfig, required])

  useEffect(() => {
    if (!propertyOptionsConfig.dependent_property) return
    if (Number(field.value) <= dependentFieldLength) return

    // Reset the value if it exceeds the number of options available based on the dependent field
    field.onChange('')
  }, [dependentFieldLength, field, propertyOptionsConfig.dependent_property])

  return (
    <TextField
      {...field}
      required={required}
      error={!!errors[propertyId]}
      label={display?.label || 'MISSING LABEL'}
      select
      helperText={display.description}
      variant={variant}
      slotProps={{
        inputLabel: {
          shrink: shrinkLabel,
          sx: {
            color: property.is_prime ? SUPER_USER_ONLY_COLOR : undefined,
          },
        },
      }}
      color={property.is_prime ? 'superUser' : undefined}
    >
      {options?.length > 0 ? (
        options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No options available.</MenuItem>
      )}
    </TextField>
  )
}
